import React from 'react';
import './Pages.css';

const EULA: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>End User License Agreement (EULA)</h1>
      </div>

      <div className="page-content legal-content">
        <p className="last-updated">Last updated: October 2025</p>

        <p>
          This End User License Agreement ("Agreement") governs your use of the GET AI PORTRAIT mobile application ("App"), provided by:
        </p>

        <div className="contact-box">
          <p>
            Dominik Reif<br />
            Waldstraße 14<br />
            91091 Großenseebach<br />
            Germany<br />
            Email: support@dreif-ai.com
          </p>
        </div>

        <p>
          By downloading, installing, or using the App, you agree to be bound by this Agreement.
          If you do not agree, do not use the App.
        </p>

        <section className="content-section">
          <h2>1. License Grant</h2>
          <p>
            You are granted a limited, non-exclusive, non-transferable, revocable license to use
            the App for personal, non-commercial purposes on any Apple-branded products that you
            own or control, in accordance with this Agreement and Apple's App Store Terms of Service.
          </p>
          <p>
            You may install and use the App on Apple-branded devices you own or control and as permitted by
            the App Store Terms.
          </p>
        </section>

        <section className="content-section">
          <h2>2. Ownership</h2>
          <p>
            All rights, title, and interest in the App (including software, content, design, and
            trademarks) remain with GET AI PORTRAIT (Dominik Reif) and its licensors. You receive
            a license only – no ownership rights are transferred.
          </p>
          <p>
            <strong>AI-Generated Portraits:</strong> You retain full ownership of any AI portraits
            generated from your photos. These portraits are created for your personal use. However,
            the AI models, style templates, and underlying technology remain our proprietary property.
          </p>
        </section>

        <section className="content-section">
          <h2>3. AI-Generated Content Disclaimer</h2>
          <p>
            The App uses artificial intelligence to generate artistic portraits from your photos.
            This content is for entertainment and artistic purposes only.
          </p>
          <ul>
            <li>AI portraits are clearly labeled as AI-generated in compliance with the EU AI Act</li>
            <li>Results may vary depending on input photo quality and selected style</li>
            <li>GET AI PORTRAIT makes no warranties as to the accuracy, quality, or artistic merit of generated portraits</li>
            <li>We do not guarantee that portraits will match your expectations</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>4. Biometric Data Processing</h2>
          <p>
            <strong>Important Notice:</strong> The App processes facial features for artistic
            transformation only. By using the App, you explicitly consent to:
          </p>
          <ul>
            <li>Face detection for portrait framing and artistic style application</li>
            <li>Temporary processing of facial landmarks (NOT for identification)</li>
            <li>Deletion of biometric data after portrait generation</li>
          </ul>
          <p>
            We do NOT create facial recognition profiles or databases. All biometric processing
            is temporary and complies with GDPR Article 9 (explicit consent for biometric data).
          </p>
        </section>

        <section className="content-section">
          <h2>5. User Content and Photos</h2>
          <p>
            You retain all rights to photos you upload to the App. By uploading photos, you:
          </p>
          <ul>
            <li>Confirm you have the right to upload and process these photos</li>
            <li>Grant us a temporary license to process photos for portrait generation</li>
            <li>Acknowledge that photos are deleted after processing (unless you save portraits)</li>
            <li>Are solely responsible for ensuring uploaded photos don't violate others' rights</li>
          </ul>
          <p>
            <strong>Prohibited Content:</strong> Do not upload photos of minors without parental
            consent, copyrighted images, or images you don't have rights to use.
          </p>
        </section>

        <section className="content-section">
          <h2>6. Subscriptions and Payments</h2>
          <p>
            The App may offer premium features via auto-renewing subscriptions managed through
            your Apple App Store account.
          </p>
          <ul>
            <li>Subscriptions renew automatically unless canceled at least 24 hours before the end of the current period</li>
            <li>Cancellations can be made through your device's subscription settings</li>
            <li>Refunds are handled according to Apple's refund policy</li>
            <li>Prices may change with reasonable notice in the App Store</li>
            <li>GET AI PORTRAIT does not process payments directly and has no access to your Apple billing data</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>7. Privacy and Data Collection</h2>
          <p>
            Your personal data is handled in accordance with the GET AI PORTRAIT Privacy Policy,
            available at:
          </p>
          <p>
            <a href="https://getaiportrait.com/privacy" target="_blank" rel="noopener noreferrer">
              https://getaiportrait.com/privacy
            </a>
          </p>
          <p>
            You acknowledge and consent that GET AI PORTRAIT and Apple may collect and use technical
            data and related information—including but not limited to technical information about your
            device, system and application software, and peripherals—that is gathered periodically to
            facilitate the provision of software updates, product support, and other services to you
            (if any) related to the Licensed Application. This information may be used to improve our
            products or to provide services or technologies to you, as long as it is in a form that
            does not personally identify you.
          </p>
        </section>

        <section className="content-section">
          <h2>8. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Reverse engineer, decompile, or modify the App</li>
            <li>Circumvent any security features or usage limits</li>
            <li>Use the App to process illegal, harmful, or offensive content</li>
            <li>Copy, redistribute, or resell AI portraits at scale without proper licensing</li>
            <li>Attempt to extract or copy AI models or style templates</li>
            <li>Use automated tools to generate portraits in bulk</li>
          </ul>
          <p>
            Violation of these rules may result in immediate termination of your license and account suspension.
          </p>
        </section>

        <section className="content-section">
          <h2>9. Termination</h2>
          <p>
            This Agreement is effective until terminated. GET AI PORTRAIT may terminate your license
            immediately if you breach any term of this Agreement. Upon termination, you must cease
            all use and delete all copies of the App.
          </p>
          <p>
            Your generated portraits remain yours even after termination, but you lose access to
            premium features and cloud-stored galleries.
          </p>
        </section>

        <section className="content-section">
          <h2>10. Disclaimer of Warranties</h2>
          <p>
            The App is provided "AS IS" and "AS AVAILABLE." To the maximum extent permitted by law,
            GET AI PORTRAIT disclaims all warranties, express or implied, including but not limited to:
          </p>
          <ul>
            <li>Fitness for a particular purpose</li>
            <li>Accuracy or quality of AI-generated portraits</li>
            <li>Uninterrupted or error-free operation</li>
            <li>Non-infringement of third-party rights</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>11. Limitation of Liability</h2>
          <p>
            To the extent permitted by applicable law, GET AI PORTRAIT shall not be liable for any
            indirect, incidental, consequential, or punitive damages, including:
          </p>
          <ul>
            <li>Loss of profits or business opportunities</li>
            <li>Loss of data or generated portraits</li>
            <li>Emotional distress from unsatisfactory AI portraits</li>
            <li>Damages from unauthorized use of uploaded photos</li>
          </ul>
          <p>
            In no event shall GET AI PORTRAIT's total aggregate liability to you for all damages
            (other than as may be required by applicable law in cases involving personal injury)
            exceed the amount of fifty dollars (U.S. $50.00).
          </p>
        </section>

        <section className="content-section">
          <h2>12. Governing Law</h2>
          <p>
            This Agreement is governed by the laws of Germany, excluding its conflict of law provisions.
            For EU consumers, mandatory consumer protection rights of your country of residence remain
            unaffected.
          </p>
        </section>

        <section className="content-section">
          <h2>13. Export Control Laws</h2>
          <p>
            You agree to comply with all applicable export and re-export control laws and regulations,
            including the Export Administration Regulations maintained by the U.S. Department of Commerce
            and trade and economic sanctions maintained by the U.S. Treasury Department's Office of Foreign
            Assets Control.
          </p>
          <p>
            You may not use or otherwise export or re-export the Licensed Application except as authorized
            by United States law and the laws of the jurisdiction in which the Licensed Application was obtained.
          </p>
          <p>
            You represent and warrant that you are not (a) located in a country that is subject to a U.S.
            Government embargo, or that has been designated by the U.S. Government as a "terrorist supporting"
            country; and (b) listed on any U.S. Government list of prohibited or restricted parties, including
            the Treasury Department's list of Specially Designated Nationals or the U.S. Department of Commerce
            Denied Persons List or Entity List.
          </p>
        </section>

        <section className="content-section">
          <h2>14. U.S. Government End Users</h2>
          <p>
            The Licensed Application and related documentation are "Commercial Items", as that term is defined
            at 48 C.F.R. §2.101, consisting of "Commercial Computer Software" and "Commercial Computer Software
            Documentation", as such terms are used in 48 C.F.R. §12.212 or 48 C.F.R. §227.7202, as applicable.
          </p>
          <p>
            Consistent with 48 C.F.R. §12.212 or 48 C.F.R. §227.7202-1 through 227.7202-4, as applicable,
            the Commercial Computer Software and Commercial Computer Software Documentation are being licensed
            to U.S. Government end users (a) only as Commercial Items and (b) with only those rights as are
            granted to all other end users pursuant to the terms and conditions herein.
          </p>
        </section>

        <section className="content-section">
          <h2>15. Third-Party Terms</h2>
          <p>Your use of the App is also subject to the terms and conditions of:</p>
          <ul>
            <li>Apple Media Services Terms and Conditions</li>
            <li>App Store Guidelines</li>
            <li>Third-party AI model providers (Replicate, OpenAI)</li>
            <li>Cloud storage providers (Firebase, CloudKit)</li>
            <li>Any applicable third-party libraries or SDKs integrated into the App</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>16. AI Act Compliance (EU Regulation 2024/1689)</h2>
          <p>
            GET AI PORTRAIT complies with the EU AI Act as a General-Purpose AI System:
          </p>
          <ul>
            <li>All AI-generated portraits are clearly labeled as AI-generated</li>
            <li>We provide transparency about AI model capabilities and limitations</li>
            <li>Users are informed about biometric data processing before use</li>
            <li>We maintain technical documentation of AI systems</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>17. Entire Agreement</h2>
          <p>
            This Agreement constitutes the entire agreement between you and GET AI PORTRAIT concerning
            the App and supersedes all prior understandings.
          </p>
        </section>

        <section className="content-section">
          <h2>18. Contact</h2>
          <p>If you have questions or concerns regarding this Agreement, contact:</p>
          <div className="contact-box">
            <p>
              Dominik Reif<br />
              support@dreif-ai.com<br />
              <a href="https://getaiportrait.com" target="_blank" rel="noopener noreferrer">
                https://getaiportrait.com
              </a>
            </p>
          </div>
        </section>

        <section className="content-section">
          <h2>19. Apple Third-Party Beneficiary Notice</h2>
          <p>
            Apple and its subsidiaries are third-party beneficiaries of this Agreement. Upon your
            acceptance, Apple shall have the right (and will be deemed to have accepted the right)
            to enforce this Agreement against you as a third-party beneficiary.
          </p>
        </section>

        <p style={{ marginTop: '40px', textAlign: 'center', color: '#999' }}>
          End of EULA
        </p>
      </div>
    </div>
  );
};

export default EULA;
